import BaseApiService from "@/shared/api/BaseApiService";
import ErrorDialog from "@/shared/dialogs/ErrorDialog";
import ErrorIndicator from "@/shared/dialogs/ErrorIndicator";
import { MainContainer } from "@/shared/structure/MainContainer";
import { TopBar } from "@/shared/structure/TopBar";
import { useGlobalStore, usePersistantStore } from "@/stores/global";
import { Save, Cancel } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Alert, Box, Button, CircularProgress } from "@mui/material";

import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useState, useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import definitions, { type Action as DefinitionAction, type SingleDefinition, type SettingsActionProps } from "./definitions";
import DynamicField from "./DynamicField";
import { settingsSnapshot } from "./settingsFormState";

function Action({ data, ...props }: { data: DefinitionAction } & SettingsActionProps) {
  if ("component" in data) {
    const Component = data.component;
    return <Component {...props} />;
  }
  return <SimpleAction data={data} {...props} />;
}

function SimpleAction({ data, id, disabled, onPendingChange }: {
  data: Exclude<DefinitionAction, { component: unknown }>;
} & SettingsActionProps) {
  const mutation = useMutation<unknown, AxiosResponse, { id: number }>({
    mutationFn: data.action,
    onMutate: () => onPendingChange(true),
    onSettled: () => onPendingChange(false),
  });
  return (
    <LoadingButton
      onClick={() => mutation.mutate({ id })}
      loading={mutation.status === "pending"}
      disabled={disabled}
      variant="text"
      startIcon={<data.icon />}
    >
      {data.title}
    </LoadingButton>
  );
}

export default function SettingsEditor() {
  const { type, rawId } = useParams();
  const region = usePersistantStore((state) => state.region);

  const definition = type ? definitions[type] as SingleDefinition : undefined;

  if (!type || !definition || (definition.regionalized && region === 0))
    return <Navigate replace to="/" />;

  const id = !rawId ? 0 : parseInt(rawId);
  return <SettingsForm key={`${type}:${id}`} type={type} id={id} definition={definition} region={region} />;
}

function SettingsForm({ type, id, definition, region }: {
  type: string; id: number; definition: SingleDefinition; region: number | null;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setPageTitle = useGlobalStore((state) => state.setPageTitle);

  const [object, setObject] = useState<Record<string, unknown>>(
    Object.fromEntries(
      definition.fields
        .map((x) => [x.id, x.default])
        .concat(definition.regionalized ? [["region", region]] : [])
    )
  );

  const [shouldFetch, setShouldFetch] = useState(id > 0);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [savedObject, setSavedObject] = useState<Record<string, unknown> | null>(null);
  const [actionPending, setActionPending] = useState(false);
  const dirty = savedSnapshot !== null && settingsSnapshot(object, definition.fields) !== savedSnapshot;

  useEffect(
    () =>
      setPageTitle(
        definition.title + ": " + (id > 0 ? "Bearbeiten" : "Erstellen")
      ),
    [definition.title, id, setPageTitle]
  );

  const query = useQuery<Record<string, unknown>, AxiosResponse>({
    queryKey: ["get" + type[0].toUpperCase() + type.slice(1), "" + id],
    queryFn: BaseApiService.getEntity(type, id, true, "settings"),
    enabled: shouldFetch,
  });

  useEffect(() => {
    if (!query.isSuccess || !query.data) return;
    setShouldFetch(false);
    setObject(query.data);
    setSavedSnapshot(settingsSnapshot(query.data, definition.fields));
    setSavedObject(query.data);
  }, [query.data, query.isSuccess, definition.fields, type]);

  const mutation = useMutation<unknown, AxiosResponse, Record<string, unknown>>({
    mutationFn:
      id > 0
        ? BaseApiService.editEntity(type, id, "settings")
        : BaseApiService.createEntity(type, true, { v: "settings" }),
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ["get" + type[0].toUpperCase() + type.slice(1) + "s"],
      });
      if (id > 0) {
        queryClient.invalidateQueries({
          queryKey: ["get" + type[0].toUpperCase() + type.slice(1), "" + id],
        });
      }
      if (definition.stayAfterSave && id > 0) {
        setSavedObject(null);
        const refreshed = await query.refetch();
        if (refreshed.isSuccess && refreshed.data) {
          setObject(refreshed.data);
          setSavedSnapshot(settingsSnapshot(refreshed.data, definition.fields));
          setSavedObject(refreshed.data);
        }
      } else {
        navigate(-1);
      }
    },
  });

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      <TopBar>
        <LoadingButton
          onClick={() => mutation.mutate(object)}
          loading={mutation.status === "pending"}
          variant="text"
          disabled={(!query.isError && shouldFetch) || query.isError || actionPending}
          startIcon={<Save />}
        >
          Speichern
        </LoadingButton>
        <Button
          onClick={() => navigate(-1 /*"/settings/users"*/)}
          variant="text"
          startIcon={<Cancel />}
        >
          Abbrechen
        </Button>
        {id > 0 && definition.actions?.map((action) => <Action key={action.key} data={action}
          id={id} savedObject={savedObject} dirty={dirty}
          disabled={shouldFetch || query.isFetching || query.isError || mutation.isPending || actionPending}
          onPendingChange={setActionPending} />)}
      </TopBar>
      <MainContainer>
        {mutation.isError && <Alert severity="error">
          Speichern fehlgeschlagen. Die Änderungen sind noch nicht gespeichert.
        </Alert>}
        {!query.isError && shouldFetch ? (
          <Box
            sx={{
              display: "flex",
              padding: 1,
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : query.isError ? (
          <ErrorIndicator />
        ) : (
          <Box>
            {definition.fields
              .filter((field) => !field.visibleFn || field.visibleFn(object))
              .map((field) => {
                const relation = "relation" in field ? field.relation : undefined;
                return (
                  <DynamicField
                    relation={typeof relation === "function" ? relation(object) : relation ?? ""}
                    disabled={
                      mutation.isPending || actionPending ||
                      (id > 0 && !!field.disableOnEdit) ||
                      (!!field.disabledFn && field.disabledFn(object))
                    }
                    value={object[field.id]}
                    region={region}
                    setObject={setObject}
                    key={field.id}
                    field={field}
                  />
                );
              })}
          </Box>
        )}
      </MainContainer>
      <ErrorDialog forceOpen={query.isError && query.error.status === 401} />
    </Box>
  );
}
